import { Preferences } from '@capacitor/preferences'

import { isNativePlatform } from './platform'

// Persistent token storage for the native app. Web clients never call these —
// they authenticate with HttpOnly cookies managed by the BFF. Native clients
// receive { access_token, refresh_token } as JSON and store them here, sending
// the access token as a Bearer header on every request.
//
// NOTE: @capacitor/preferences is app-sandboxed but not encrypted at rest. This
// is acceptable for short-lived access tokens; a secure-storage plugin can be
// swapped in later without changing callers.

const ACCESS_KEY = 'crapapp.accessToken'
const REFRESH_KEY = 'crapapp.refreshToken'

// In-memory cache so the request hot-path can attach the token synchronously
// without awaiting disk on every call.
let accessTokenCache: string | null = null
let refreshTokenCache: string | null = null
let hydrated = false

// Loads persisted tokens into the in-memory cache. Must be awaited once at app
// startup on native before the first authenticated request. No-op on web.
export async function hydrateTokens(): Promise<void> {
    if (!isNativePlatform() || hydrated) return
    const [a, r] = await Promise.all([
        Preferences.get({ key: ACCESS_KEY }),
        Preferences.get({ key: REFRESH_KEY }),
    ])
    accessTokenCache = a.value
    refreshTokenCache = r.value
    hydrated = true
}

export function getAccessTokenSync(): string | null {
    return accessTokenCache
}

export function getRefreshTokenSync(): string | null {
    return refreshTokenCache
}

// Persists a new access token (and optionally a rotated refresh token).
export async function setTokens(access: string, refresh?: string): Promise<void> {
    accessTokenCache = access || null
    if (access) await Preferences.set({ key: ACCESS_KEY, value: access })
    else await Preferences.remove({ key: ACCESS_KEY })

    if (refresh !== undefined) {
        refreshTokenCache = refresh || null
        if (refresh) await Preferences.set({ key: REFRESH_KEY, value: refresh })
        else await Preferences.remove({ key: REFRESH_KEY })
    }
}

export async function clearTokens(): Promise<void> {
    accessTokenCache = null
    refreshTokenCache = null
    await Promise.all([
        Preferences.remove({ key: ACCESS_KEY }),
        Preferences.remove({ key: REFRESH_KEY }),
    ])
}
